import React, { useMemo } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { useAuth } from "../auth/AuthContext";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import CategoryIcon from "@mui/icons-material/Category";
import CampaignIcon from "@mui/icons-material/Campaign";
import ExploreIcon from "@mui/icons-material/Explore";
import SettingsIcon from "@mui/icons-material/Settings";
import AppSettingsAltIcon from "@mui/icons-material/AppSettingsAlt";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import TuneIcon from "@mui/icons-material/Tune";
import MapIcon from "@mui/icons-material/Map";
import InsightsIcon from "@mui/icons-material/Insights";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";

import Home from "../pages/Home";
import Analytics from "../pages/Analytics";
import UserList from "../pages/UserList";
import DriverList from "../pages/DriverList";
import StaffAdminPage from "../pages/StaffAdminPage";
import LoginPage from "../pages/LoginPage";
import PrivateRoute from "../components/PrivateRoute";
import { logout } from "../services/authService";
import { isAuthenticated } from "../utils/auth";
import RideList from "../pages/RideList";
import RichNavDrawer from "../components/RichNavDrawer";
import AppConfigList from "../pages/AppConfigList";
import RideOptionList from "../pages/RideOptionList";
import RateCards from "../pages/RateCards";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MarketingList from "../pages/MarketingList";
import TabbedSectionAdmin from "../pages/TabbedSectionAdmin";
import WalletList from "../pages/WalletList";
import SupportTicketList from "../pages/SupportTicketList";
import SupportTicketDetail from "../pages/SupportTicketDetail";
import RideDetail from "../pages/RideDetail";
import UserDetail from "../pages/UserDetail";
import DriverDetail from "../pages/DriverDetail";
import NotificationComposer from "../pages/NotificationComposer";
import SubscriptionPlans from "../pages/SubscriptionPlans";
import BusinessSettings from "../pages/BusinessSettings";
import ZoneExplorer from "../pages/ZoneExplorer";
import ZoneDetails from "../pages/ZoneDetails";
import CityOperations from "../pages/CityOperations";
import AuditLogList from "../pages/AuditLogList";
import HistoryIcon from "@mui/icons-material/History";
import FinanceOps from "../pages/FinanceOps";
import LiveOps from "../pages/LiveOps";
import PaymentsIcon from "@mui/icons-material/Payments";
import SensorsIcon from "@mui/icons-material/Sensors";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DriverPayouts from "../pages/DriverPayouts";
import PromoList from "../pages/PromoList";
import ReportCenter from "../pages/ReportCenter";
import QrInsights from "../pages/QrInsights";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AssessmentIcon from "@mui/icons-material/Assessment";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SafetySos from "../pages/SafetySos";

const drawerWidth = 235;

// `permission` (optional) gates visibility; items without one are always shown.
// ANY-OF semantics when an array is provided.
const menuItems = [
    { text: "Dashboard", icon: DashboardIcon, path: "/", color: "#24b9d4" },
    { text: "Analytics", icon: InsightsIcon, path: "/analytics", color: "#10B981", permission: "analytics:read" },
    { text: "Support Tickets", icon: SupportAgentIcon, path: "/support-tickets", color: "#24b9d4", permission: "support:read" },
    { text: "Safety / SOS", icon: ReportProblemIcon, path: "/safety-sos", color: "#ef4444", permission: "support:read" },
    { text: "Users", icon: PeopleIcon, path: "/users", color: "#24b9d4", permission: "users:read" },
    { text: "Drivers", icon: PersonIcon, path: "/drivers", color: "#24b9d4", permission: "drivers:read" },
    { text: "Staff & Admins", icon: AdminPanelSettingsIcon, path: "/staff", color: "#ef4444", permission: "staff:read" },
    { text: "City Operations", icon: BusinessCenterIcon, path: "/city-operations", color: "#f59e0b", permission: "ops:bulk" },
    { text: "Live Ops", icon: SensorsIcon, path: "/live-ops", color: "#10B981", permission: "rides:read", isNew: true },
    { text: "Finance", icon: PaymentsIcon, path: "/finance", color: "#f59e0b", permission: ["wallet:read", "payments:read"], isNew: true },
    { text: "Driver Payouts", icon: AccountBalanceWalletIcon, path: "/driver-payouts", color: "#10B981", permission: "drivers:read", isNew: true },
    { text: "Rides", icon: LocalTaxiIcon, path: "/rides", color: "#24b9d4", permission: "rides:read" },
    { text: "Ride Option", icon: DriveEtaIcon, path: "/rides-options", color: "#f59e0b", permission: "pricing:read" },
    { text: "Rate Cards", icon: ReceiptLongIcon, path: "/rate-cards", color: "#f59e0b", permission: "pricing:read", isNew: true },
    { text: "Zones", icon: MapIcon, path: "/zones", color: "#f59e0b", permission: "zones:read" },
    {
        text: "System Health",
        icon: TuneIcon,
        path: "/queues",
        isExternal: true,
        color: "#ef4444",
        permission: "config:write",
    },
    { text: "Push Notifications", icon: NotificationsActiveIcon, path: "/notifications/send", color: "#8b5cf6", permission: "notifications:send" },
    { text: "Promo Codes", icon: LocalOfferIcon, path: "/promos", color: "#8b5cf6", permission: "promos:read", isNew: true },
    { text: "Reports", icon: AssessmentIcon, path: "/reports", color: "#10B981", permission: ["rides:read", "payments:read", "wallet:read", "audit:read"], isNew: true },
    { text: "QR Insights", icon: QrCode2Icon, path: "/qr-insights", color: "#10B981", permission: "ops:bulk", isNew: true },

    {
        text: "Categories",
        icon: CategoryIcon,
        color: "#8b5cf6",
        children: [
            { text: "Marketing", icon: CampaignIcon, path: "/marketing", color: "#8b5cf6", permission: "content:read" },
            { text: "Explore Items", icon: ExploreIcon, path: "/explore-list", color: "#8b5cf6", permission: "content:read" },
        ],
    },

    {
        text: "Settings",
        icon: SettingsIcon,
        color: "#94a3b8",
        children: [
            { text: "App Config", icon: AppSettingsAltIcon, path: "/app-config", color: "#94a3b8", permission: "config:read" },
            { text: "Revenue Plans", icon: MonetizationOnIcon, path: "/subscription-plans", color: "#f59e0b", permission: "subscriptions:read" },
            { text: "Business Logic", icon: TuneIcon, path: "/business-settings", color: "#94a3b8", permission: "subscriptions:read" },
            { text: "Audit Log", icon: HistoryIcon, path: "/audit-logs", color: "#94a3b8", permission: "audit:read", isNew: true },
        ],
    },
];


interface AppRoutesProps {
    mobileNavOpen?: boolean;
    onMobileNavClose?: () => void;
}

const AppRoutes: React.FC<AppRoutesProps> = ({ mobileNavOpen = false, onMobileNavClose }) => {
    const location = useLocation();
    const { hasAnyPermission, loading } = useAuth();
    const showDrawer = isAuthenticated() && location.pathname !== "/login";

    // Filter nav by the staff member's effective permissions. Parent groups are
    // shown only when at least one child is visible. While /admin/me loads, show
    // the full menu to avoid a flash of empty nav.
    const visibleMenuItems = useMemo(() => {
        const canSee = (perm?: string | string[]) => {
            if (!perm || loading) return true;
            return hasAnyPermission((Array.isArray(perm) ? perm : [perm]) as any);
        };
        return menuItems
            .map((item: any) => {
                if (item.children) {
                    const children = item.children.filter((c: any) => canSee(c.permission));
                    return children.length ? { ...item, children } : null;
                }
                return canSee(item.permission) ? item : null;
            })
            .filter(Boolean);
    }, [hasAnyPermission, loading]);

    return (
        <Box sx={{ display: "flex" }}>
            {showDrawer && (
                <RichNavDrawer
                    menuItems={visibleMenuItems as any}
                    onLogout={logout}
                    drawerWidth={drawerWidth}
                    mobileOpen={mobileNavOpen}
                    onMobileClose={onMobileNavClose}
                />
            )}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    px: { xs: 1, sm: 3, md: 4 },
                    py: { xs: 2, sm: 3 },
                    minHeight: '100vh',
                    overflowX: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: "#f5f7fa"
                }}
            >
                <Routes>
                    <Route path="/login" element={<LoginPage />} />

                    {/* Core Routes */}
                    <Route element={<PrivateRoute><Home /></PrivateRoute>} path="/" />
                    <Route element={<PrivateRoute permission="analytics:read"><Analytics /></PrivateRoute>} path="/analytics" />
                    <Route element={<PrivateRoute permission="users:read"><UserList /></PrivateRoute>} path="/users" />
                    <Route element={<PrivateRoute permission="users:read"><UserDetail /></PrivateRoute>} path="/user/:id" />
                    <Route element={<PrivateRoute permission="drivers:read"><DriverList /></PrivateRoute>} path="/drivers" />
                    <Route element={<PrivateRoute permission="drivers:read"><DriverDetail /></PrivateRoute>} path="/driver/:id" />
                    <Route element={<PrivateRoute permission="staff:read"><StaffAdminPage /></PrivateRoute>} path="/staff" />
                    <Route element={<PrivateRoute permission="ops:bulk"><CityOperations /></PrivateRoute>} path="/city-operations" />
                    <Route element={<PrivateRoute permission="rides:read"><LiveOps /></PrivateRoute>} path="/live-ops" />
                    <Route element={<PrivateRoute permission={["wallet:read", "payments:read"]}><FinanceOps /></PrivateRoute>} path="/finance" />
                    <Route element={<PrivateRoute permission="drivers:read"><DriverPayouts /></PrivateRoute>} path="/driver-payouts" />
                    <Route element={<PrivateRoute permission="rides:read"><RideList /></PrivateRoute>} path="/rides" />
                    <Route element={<PrivateRoute permission="rides:read"><RideDetail /></PrivateRoute>} path="/ride/:id" />
                    <Route element={<PrivateRoute permission="wallet:read"><WalletList /></PrivateRoute>} path="/wallet-transactions" />

                    {/* Management Routes */}
                    <Route element={<PrivateRoute permission="pricing:read"><RideOptionList /></PrivateRoute>} path="/rides-options" />
                    <Route element={<PrivateRoute permission="pricing:read"><RateCards /></PrivateRoute>} path="/rate-cards" />
                    <Route element={<PrivateRoute permission="zones:read"><ZoneExplorer /></PrivateRoute>} path="/zones" />
                    <Route element={<PrivateRoute permission="zones:read"><ZoneDetails /></PrivateRoute>} path="/zones/:id" />
                    <Route element={<PrivateRoute permission="content:read"><MarketingList /></PrivateRoute>} path="/marketing" />
                    <Route element={<PrivateRoute permission="content:read"><TabbedSectionAdmin /></PrivateRoute>} path="/explore-list" />
                    <Route element={<PrivateRoute permission="support:read"><SupportTicketList /></PrivateRoute>} path="/support-tickets" />
                    <Route element={<PrivateRoute permission="support:read"><SupportTicketDetail /></PrivateRoute>} path="/support-ticket/:id" />
                    <Route element={<PrivateRoute permission="support:read"><SafetySos /></PrivateRoute>} path="/safety-sos" />
                    <Route element={<PrivateRoute permission="notifications:send"><NotificationComposer /></PrivateRoute>} path="/notifications/send" />
                    <Route element={<PrivateRoute permission="promos:read"><PromoList /></PrivateRoute>} path="/promos" />
                    <Route element={<PrivateRoute permission={["rides:read", "payments:read", "wallet:read", "audit:read"]}><ReportCenter /></PrivateRoute>} path="/reports" />
                    <Route element={<PrivateRoute permission="ops:bulk"><QrInsights /></PrivateRoute>} path="/qr-insights" />

                    {/* Settings Routes */}
                    <Route element={<PrivateRoute permission="config:read"><AppConfigList /></PrivateRoute>} path="/app-config" />
                    <Route element={<PrivateRoute permission="subscriptions:read"><SubscriptionPlans /></PrivateRoute>} path="/subscription-plans" />
                    <Route element={<PrivateRoute permission="subscriptions:read"><BusinessSettings /></PrivateRoute>} path="/business-settings" />
                    <Route element={<PrivateRoute permission="audit:read"><AuditLogList /></PrivateRoute>} path="/audit-logs" />
                </Routes>
            </Box>
        </Box>
    );
};

export default AppRoutes;